import React, { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import { FiRotateCcw } from "react-icons/fi";
import ReturnsSectionMobile from "./ReturnsSectionMobile";
import styles from "./ReturnsSection.module.css";

// Мап статусів для відображення (можеш розширити)
const STATUS_MAP = {
  Повернено: "Повернено",
  "Частково повернено": "Частково повернено",
};

function getReturnAmount(returnedItems) {
  return returnedItems.reduce(
    (sum, item) =>
      sum + (item.totalReturnAmount || item.returnQuantity * item.price),
    0
  );
}

// Деталі повернення (розгорнутий рядок)
const ReturnDetails = ({ order }) => (
  <div className={styles.returnDetailsCard}>
    <div className={styles.returnDetailsTablesWrap}>
      <table className={styles.returnDetailsTable}>
        <thead>
          <tr>
            <th>Артикул</th>
            <th>Назва</th>
            <th>Розмір упаковки</th>
            <th>Ціна, ₴</th>
            <th>Кількість</th>
            <th>Сума повернення, ₴</th>
            <th>Дата повернення</th>
          </tr>
        </thead>
        <tbody>
          {order.returnedItems.map((item, idx) => (
            <tr key={item.productId || idx}>
              <td>{item.productCode}</td>
              <td>{item.name}</td>
              <td>{item.weight || "-"}</td>
              <td>{item.price}</td>
              <td>{item.returnQuantity}</td>
              <td className={styles.returnMinus}>
                -{item.totalReturnAmount || item.returnQuantity * item.price}
              </td>
              <td>
                {item.returnDate
                  ? new Date(item.returnDate).toLocaleString("uk-UA")
                  : "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ReturnsSection = () => {
  const [returns, setReturns] = useState([]);
  const [openDetails, setOpenDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Мобільний перемикач
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Завантаження повернень з API
  useEffect(() => {
    const fetchReturns = async () => {
      try {
        const res = await axiosInstance.get("/customers/me/orders");
        // Залишаємо лише замовлення з поверненнями
        const onlyReturns = (res.data || [])
          .filter(
            (order) =>
              Array.isArray(order.returnedItems) &&
              order.returnedItems.length > 0
          )
          .map((order) => ({
            ...order,
            createdAt: order.returnedItems[0]?.returnDate, // для дати повернення
            totalReturnAmount: getReturnAmount(order.returnedItems),
          }));
        setReturns(onlyReturns);
      } catch {
        setError("Помилка при отриманні повернень");
      } finally {
        setLoading(false);
      }
    };
    fetchReturns();
  }, []);

  const toggleDetails = (orderId) => {
    setOpenDetails(openDetails === orderId ? null : orderId);
  };

  if (loading) return <div>Завантаження повернень…</div>;
  if (error) return <div style={{ color: "red" }}>{error}</div>;

  const sectionHeader = (
    <div className={styles.sectionHeader}>
      <div className={styles.sectionIcon}>
        <FiRotateCcw />
      </div>
      <div>
        <span>Сервіс</span>
        <h1>Повернення товарів</h1>
        <p>Історія повернених товарів, статуси та суми повернення за замовленнями.</p>
      </div>
    </div>
  );

  // --- Мобільна версія
  if (isMobile) {
    return (
      <div className={styles.returnsRoot}>
        {sectionHeader}
        <ReturnsSectionMobile returns={returns} />
      </div>
    );
  }

  // --- Десктопна версія ---
  if (!returns.length)
    return (
      <div className={styles.returnsRoot}>
        {sectionHeader}
        <div className={styles.emptyReturnsMsg}>У вас ще немає повернень.</div>
      </div>
    );

  return (
    <div className={styles.returnsRoot}>
      {sectionHeader}
      <div style={{ overflowX: "auto" }}>
        <table className={styles.returnsTable}>
          <thead>
            <tr>
              <th>№ замовлення</th>
              <th>Дата та час повернення</th>
              <th>Сума повернення, ₴</th>
              <th>Статус замовлення</th>
              <th>Переглянути</th>
            </tr>
          </thead>
          <tbody>
            {returns.map((order) => (
              <React.Fragment key={order._id}>
                <tr>
                  <td className={styles.returnsTableValue} data-label="№">
                    {order.orderNumber}
                  </td>
                  <td className={styles.returnsTableValue}>
                    {order.createdAt
                      ? new Date(order.createdAt).toLocaleString("uk-UA")
                      : "-"}
                  </td>
                  <td className={styles.returnsTableValue}>
                    {order.totalReturnAmount}
                  </td>
                  <td className={styles.returnsTableValue}>
                    {STATUS_MAP[order.status] || order.status}
                  </td>
                  <td className={styles.returnsTableValue}>
                    <button
                      onClick={() => toggleDetails(order._id)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                      aria-label="Деталі повернення"
                    >
                      {openDetails === order._id ? (
                        <IoChevronUp />
                      ) : (
                        <IoChevronDown />
                      )}
                    </button>
                  </td>
                </tr>
                {openDetails === order._id && (
                  <tr>
                    <td colSpan={5}>
                      <ReturnDetails order={order} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReturnsSection;
